import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCostTable } from '../api/api';
import { formatCurrency } from '../utils/formatters';
import {
    Box, Typography, Paper, Grid, TextField, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Chip, CircularProgress, Alert, Stack, Button,
    Tabs, Tab, Pagination, Breadcrumbs, Link
} from '@mui/material';
import {
    Search as SearchIcon,
    ArrowBack as ArrowBackIcon,
    Inventory as InsumosIcon,
    Medication as ProcedimentosIcon,
    FileDownload as FileDownloadIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';

const CostTablesViewPage = () => {
    const { mes } = useParams();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({ items: [], summary: {}, total: 0 });
    
    // Filters & Pagination
    const [tipo, setTipo] = useState('insumo'); // 'insumo' | 'procedimento'
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const perPage = 100;

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getCostTable(mes, {
                tipo,
                search,
                page,
                per_page: perPage
            });
            setData(res.data);
        } catch (err) {
            console.error("Erro ao carregar tabela de custos:", err);
            setError(err.response?.data?.error || "Erro ao carregar os dados da tabela de custos.");
        } finally {
            setLoading(false);
        }
    }, [mes, tipo, search, page]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTipoChange = (event, newValue) => {
        setTipo(newValue);
        setPage(1); // Reset to first page on tab change
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleExportCSV = () => {
        if (!data.items || data.items.length === 0) return;

        const columns = [
            { key: 'tipo', label: 'Tipo' },
            { key: 'codigo', label: 'Código' },
            { key: 'descricao', label: 'Descrição' },
            { key: 'centro_custo', label: 'Centro de Custo' },
            { key: 'quantidade', label: 'Quantidade' },
            { key: 'valor_receita', label: 'Receita Total' },
            { key: 'custo_total', label: 'Custo Total' },
            { key: 'custo_unitario', label: 'Custo Unitário' },
            { key: 'custo_medio_insumo', label: 'Custo Médio (Insumo)' }
        ];

        const headers = columns.map(c => c.label).join(';');
        const rows = data.items.map(row =>
            columns.map(c => {
                const val = row[c.key];
                return typeof val === 'number'
                    ? val.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                    : val || '';
            }).join(';')
        );
        
        const csv = '\uFEFF' + [headers, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `tabela_custos_${mes}_${tipo}.csv`;
        link.click();
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header & Breadcrumbs */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigate('/competencias')} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        <ArrowBackIcon sx={{ mr: 0.5, fontSize: 'inherit' }} /> Gerenciamento
                    </Link>
                    <Typography color="text.primary">Tabela de Custos ({mes})</Typography>
                </Breadcrumbs>
                
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', mb: 1 }}>
                            Tabela de Custos Gerada
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Detalhamento estático gerado no fechamento da competência <strong>{mes}</strong>.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button 
                            variant="outlined" 
                            startIcon={<RefreshIcon />} 
                            onClick={fetchData}
                            disabled={loading}
                        >
                            Atualizar
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={<FileDownloadIcon />} 
                            onClick={handleExportCSV}
                            disabled={loading || data.total === 0}
                        >
                            Exportar CSV
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {['insumo', 'procedimento'].map((t) => (
                    <Grid item xs={12} md={6} key={t}>
                        <Paper sx={{ 
                            p: 3, 
                            borderRadius: 4, 
                            bgcolor: tipo === t ? 'primary.main' : 'background.paper',
                            color: tipo === t ? 'white' : 'text.primary',
                            transition: 'all 0.3s ease',
                            border: '1px solid',
                            borderColor: tipo === t ? 'primary.main' : 'divider',
                            cursor: 'pointer'
                        }} onClick={() => setTipo(t)}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box sx={{ 
                                    p: 1.5, 
                                    borderRadius: 3, 
                                    bgcolor: tipo === t ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)'
                                }}>
                                    {t === 'insumo' ? <InsumosIcon fontSize="large" /> : <ProcedimentosIcon fontSize="large" />}
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 1.5 }}>
                                        {t === 'insumo' ? 'Insumos' : 'Procedimentos'}
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                        {data.summary[t]?.qtd_itens || 0} Itens
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Custo Total</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(data.summary[t]?.total_custo || 0)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Content Tabs & Filters */}
            <Paper sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'rgba(0,0,0,0.02)' }}>
                    <Tabs value={tipo} onChange={handleTipoChange} textColor="primary" indicatorColor="primary">
                        <Tab value="insumo" label="Insumos" icon={<InsumosIcon />} iconPosition="start" />
                        <Tab value="procedimento" label="Procedimentos" icon={<ProcedimentosIcon />} iconPosition="start" />
                    </Tabs>
                    
                    <Box sx={{ p: 1, display: 'flex', gap: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Buscar por descrição..."
                            value={search}
                            onChange={handleSearchChange}
                            sx={{ width: 300 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Box>

                <TableContainer sx={{ maxHeight: 'calc(100vh - 450px)' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Centro de Custo</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Qtd</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Custo Unit.</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Custo Total</TableCell>
                                {tipo === 'insumo' && (
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Custo Médio Est.</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={tipo === 'insumo' ? 7 : 6} align="center" sx={{ py: 10 }}>
                                        <CircularProgress size={32} />
                                        <Typography sx={{ mt: 2 }} color="text.secondary">Carregando dados...</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : data.items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={tipo === 'insumo' ? 7 : 6} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">Nenhum registro encontrado.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                data.items.map((item) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell>
                                            <Chip label={item.codigo} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{item.descricao}</TableCell>
                                        <TableCell color="text.secondary">{item.centro_custo}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                            {item.quantidade.toLocaleString('pt-BR')}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                            {formatCurrency(item.custo_unitario)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            {formatCurrency(item.custo_total)}
                                        </TableCell>
                                        {tipo === 'insumo' && (
                                            <TableCell align="right" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                                {formatCurrency(item.custo_medio_insumo)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'rgba(0,0,0,0.02)' }}>
                    <Typography variant="body2" color="text.secondary">
                        Mostrando {data.items.length} de {data.total} registros
                    </Typography>
                    <Pagination 
                        count={Math.ceil(data.total / perPage)} 
                        page={page} 
                        onChange={(e, v) => setPage(v)} 
                        color="primary" 
                        size="small"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            </Paper>
            
            {error && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
        </Box>
    );
};

export default CostTablesViewPage;
